import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { addCustomerLog } from '@libs/NwLogs';
import { getVisibilityNotes } from '@libs/NwNotes';

import { useStateValue } from '@contexts/StateProvider';

import { useContact } from '@hooks/API/Contact/UseContact';
import { useContactConnections } from '@hooks/API/Contact/UseContactConnections';

import ContactDetailsTasks from '@components/ContactDetails/CommonComponents/ContactDetailsTasks';
import CustomerDetailsConnections from '@components/ContactDetails/CustomerDetails/CommonComponents/CustomerDetailsConnections';
import CustomerModelList from '@components/ContactDetails/CustomerDetails/CommonComponents/CustomerModelList';

import Comments from '@components/Comments/Comments';
import ContactDetailsActions from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsActions';
import CustomerDetailsHeaderDrawer from '@components/ContactDetails/ContactDetailsHeader/CustomerDetailsHeaderDrawer';
import ContactDetailsNotes from '@components/ContactDetailsNotes/ContactDetailsNotes';
import ObjectHistory from '@components/CreatedBy/ObjectHistory';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import { NwCardTabs } from '@components/Gui/NwCard/NwCardTabs';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import ContactSocials from '@components/Social/ContactSocials';

// import TaskCardsGrid from '@components/TaskList/TaskCardsGrid';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Actions = styled(FlexContainer)`
    gap: 2rem;
    margin-right: 1rem;
`;

const CustomerDrawer = ({ customerID, onClose }) => {
    const { data: currentContact } = useContact(customerID);
    const { data: currentContactConnections, isFetching: isFetchingConnections } = useContactConnections(customerID);

    const [{ areaContext }] = useStateValue();
    const navigate = useNavigate();
    const [textTab, setTextTab] = useState('comments');

    useEffect(() => {
        if (currentContact) {
            addCustomerLog(currentContact, areaContext.area);
        }

    }, [currentContact]);

    const handleMoveContactDetails = () => {
        if (areaContext.area === 'management') {
            navigate(`/management-customers/${customerID}`);
        } else {
            navigate(`/customer/${customerID}`);
        }
    };

    const getTextsTabs = () => {
        return [
            {
                name: 'Comments',
                key: 'comments',
                counter: currentContact.Comments.length,
            },
            {
                name: 'Notes',
                key: 'notes',
                counter: getVisibilityNotes(currentContact ? currentContact.Notes : [], areaContext.area).length,
            },
        ];
    };

    const renderTextsTabs = () => {
        if (textTab === 'notes') {
            return <ContactDetailsNotes isInCardTab drawerMode currentContact={currentContact} readOnly />;
        } else {
            return (
                <Comments
                    isInCardTab
                    api="ContactID"
                    contactId={customerID}
                    ID={customerID}
                    readOnly
                    drawerMode
                    comments={currentContact ? currentContact.Comments : []}
                />
            );
        }
    };

    return (
        <NwDrawer
            width={780}
            title={
                currentContact ? (
                    <FlexContainer justifyContent="space-between">
                        <div>Contact Preview</div>
                        <Actions>
                            {areaContext.area !== 'management' && (
                                <>
                                    <NwIcon
                                        icon={byPrefixAndName.fass['expand-wide']}
                                        pointer
                                        onClick={handleMoveContactDetails}
                                        size="lg"
                                    />
                                    <ObjectHistory iconOnly onIconClick={() => {}} object={currentContact} />
                                    <ContactDetailsActions contact={currentContact} fromDrawer onClose={onClose} />
                                </>
                            )}
                        </Actions>
                    </FlexContainer>
                ) : (
                    'Contact Preview'
                )
            }
            onClose={onClose}
            whiteBg={false}
        >
            <div className="drawer-content">
                {!currentContact ? (
                    <Loading />
                ) : (
                    <>
                        <CustomerDetailsHeaderDrawer contactID={customerID} />
                        <div className="content-section">
                            <ContactSocials previewMode currentContact={currentContact} />
                        </div>
                        <div className="content-section">
                            <ContactDetailsTasks currentContact={currentContact} />
                        </div>
                        {/* <div className="content-section">
                            <TaskCardsGrid drawerMode contactType="model" previewMode contactId={currentContact.ID} />
                        </div> */}
                        <div className="content-section">
                            {isFetchingConnections ? (
                                <Loading />
                            ) : (
                                <CustomerDetailsConnections
                                    currentContact={currentContact}
                                    currentContactConnections={currentContactConnections}
                                    readOnly
                                    fluid
                                    contactID={currentContact.ID}
                                />
                            )}
                        </div>
                        {currentContact.SubType === 2 && (
                            <div className="content-section">
                                <CustomerModelList contactID={currentContact.ID} fluid />
                            </div>
                        )}
                        <div className="content-section">
                            <NwCardTabs tabs={getTextsTabs()} activeTab={textTab} onTabChange={setTextTab} fluid>
                                {renderTextsTabs()}
                            </NwCardTabs>
                        </div>
                    </>
                )}
            </div>
        </NwDrawer>
    );
};

export default CustomerDrawer;
